import { useMemo, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { GraduationCap, Check, X } from "lucide-react";

// Canonical order for age categories — youth first, then senior
const CATEGORY_ORDER = [
  "U7", "U9", "U11", "U13", "U15", "U16", "U17", "U18", "U19", "U20", "U21",
  "M21", "Junior", "Senior", "Vétérans", "Sans catégorie",
];

const sortByOrder = (a, b) => {
  const ia = CATEGORY_ORDER.indexOf(a);
  const ib = CATEGORY_ORDER.indexOf(b);
  if (ia === -1 && ib === -1) return a.localeCompare(b);
  if (ia === -1) return 1;
  if (ib === -1) return -1;
  return ia - ib;
};

/**
 * Premium category filter — groups players by age category (U13, U15, Senior…)
 *
 * Props:
 *  - players: full visible players array
 *  - activeCategory: current selected category (or "__all__")
 *  - onChange: (categoryName) => void
 */
export default function CategoryFilter({ players, activeCategory, onChange }) {
  const [open, setOpen] = useState(false);

  const categories = useMemo(() => {
    const map = new Map();
    players.forEach((p) => {
      const key = (p.category || "").trim() || "Sans catégorie";
      map.set(key, (map.get(key) || 0) + 1);
    });
    return Array.from(map.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => sortByOrder(a.name, b.name));
  }, [players]);

  const total = players.length;
  const activeCat =
    activeCategory === "__all__"
      ? null
      : categories.find((c) => c.name === activeCategory);

  // Hide if there's only "Sans catégorie" or empty
  if (categories.length <= 1 && categories[0]?.name === "Sans catégorie") {
    return null;
  }

  const select = (name) => {
    onChange(name);
    setOpen(false);
  };

  return (
    <div className="inline-flex items-center gap-2" data-testid="category-filter">
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>
          <button
            type="button"
            data-testid="category-filter-trigger"
            className="inline-flex items-center gap-2.5 h-10 px-4 rounded-full bg-white/[0.04] border border-white/15 text-white hover:border-white/30 hover:bg-white/[0.08] transition-all"
          >
            <GraduationCap className="w-3.5 h-3.5 text-zinc-400" />
            <span className="text-xs uppercase tracking-[0.18em] font-bold">
              {activeCat ? activeCat.name : "Toutes catégories"}
            </span>
            <span
              className="text-[10px] font-bold tabular-nums px-1.5 py-0.5 rounded-md bg-white/10 text-zinc-300"
              data-testid="category-filter-count"
            >
              {activeCat ? activeCat.count : total}
            </span>
          </button>
        </SheetTrigger>

        <SheetContent
          side="right"
          className="bg-[#0A0B0E] border-l border-white/10 text-white p-0 w-full sm:max-w-md"
          data-testid="category-filter-sheet"
        >
          <SheetHeader className="px-5 pt-6 pb-3 border-b border-white/10">
            <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#D4AF37] mb-1.5">
              Laser Tech Création
            </div>
            <SheetTitle className="font-display text-2xl font-black uppercase tracking-tight">
              Filtrer par catégorie
            </SheetTitle>
          </SheetHeader>

          {/* "All categories" reset */}
          <div className="px-5 pt-4 pb-2">
            <button
              type="button"
              onClick={() => select("__all__")}
              data-testid="category-filter-all"
              className="w-full flex items-center justify-between gap-3 rounded-lg px-3 py-2.5 hover:bg-white/[0.05] transition-colors"
              style={{
                background: activeCategory === "__all__" ? "rgba(255,255,255,0.06)" : "transparent",
                border: activeCategory === "__all__" ? "1px solid rgba(255,255,255,0.2)" : "1px solid transparent",
              }}
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full flex items-center justify-center bg-white/[0.05] border border-white/10">
                  <GraduationCap className="w-3.5 h-3.5 text-zinc-300" />
                </div>
                <span className="font-display text-sm font-bold uppercase tracking-tight">
                  Toutes les catégories
                </span>
              </div>
              <span className="flex items-center gap-2">
                <span className="text-[10px] tabular-nums px-1.5 py-0.5 rounded bg-white/10 text-zinc-300 font-bold">
                  {total}
                </span>
                {activeCategory === "__all__" && <Check className="w-4 h-4 text-white" />}
              </span>
            </button>
          </div>

          {/* Sub-section: "Jeunes" (U-something) vs "Adultes" */}
          <div className="overflow-y-auto max-h-[calc(100vh-200px)] pb-8">
            {(() => {
              const youth = categories.filter((c) => /^U\d/.test(c.name));
              const adults = categories.filter((c) => !/^U\d/.test(c.name));
              return (
                <>
                  {youth.length > 0 && (
                    <CategorySection
                      label="Jeunes"
                      list={youth}
                      activeCategory={activeCategory}
                      onSelect={select}
                    />
                  )}
                  {adults.length > 0 && (
                    <CategorySection
                      label="Adultes"
                      list={adults}
                      activeCategory={activeCategory}
                      onSelect={select}
                    />
                  )}
                </>
              );
            })()}
          </div>
        </SheetContent>
      </Sheet>

      {activeCat && (
        <button
          type="button"
          onClick={() => onChange("__all__")}
          data-testid="category-filter-clear"
          className="inline-flex items-center gap-2 h-10 px-3 rounded-full text-xs font-bold uppercase tracking-[0.15em] transition-all bg-white/[0.06] border border-white/20 text-white hover:bg-white/[0.10]"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-white/60" />
          {activeCat.name}
          <X className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}

const CategorySection = ({ label, list, activeCategory, onSelect }) => (
  <div className="px-5 pt-4">
    <div className="flex items-center gap-2 mb-2">
      <span className="w-1.5 h-1.5 rounded-full bg-white/40" />
      <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-500">
        {label} · {list.length}
      </span>
    </div>
    <div className="space-y-1">
      {list.map((c) => (
        <button
          key={c.name}
          type="button"
          onClick={() => onSelect(c.name)}
          data-testid={`category-filter-item-${c.name.replace(/\s+/g, "-").toLowerCase()}`}
          className="w-full flex items-center justify-between gap-3 rounded-lg px-3 py-2 hover:bg-white/[0.05] transition-colors"
          style={{
            background: activeCategory === c.name ? "rgba(255,255,255,0.06)" : "transparent",
            border: activeCategory === c.name ? "1px solid rgba(255,255,255,0.2)" : "1px solid transparent",
          }}
        >
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 bg-white/[0.05] border border-white/10">
              <span className="font-display font-black text-[10px] text-zinc-300">
                {c.name.startsWith("U") ? c.name : c.name[0]}
              </span>
            </div>
            <span className="font-body text-sm text-white truncate text-left">
              {c.name}
            </span>
          </div>
          <span className="flex items-center gap-2 shrink-0">
            <span className="text-[10px] tabular-nums px-1.5 py-0.5 rounded bg-white/10 text-zinc-300 font-bold">
              {c.count}
            </span>
            {activeCategory === c.name && <Check className="w-4 h-4 text-white" />}
          </span>
        </button>
      ))}
    </div>
  </div>
);
